import React from 'react'
import { cn } from '@/lib/utils'
import '../sections/control-targets3.css'

/**
 * MarketPositionStrip (W9, Market intelligence) — a comp-set readout with a small
 * position indicator showing where your rate sits against the market median.
 *
 * Copy is quoted verbatim from docs/marketing/2026-07-05-landing3-copy.md §W9.
 * All figures (You £120, Market median £132) are illustrative fiction. The position
 * marker slides to its spot ONCE on reveal via an IntersectionObserver `is-live` gate;
 * reduced motion resolves to the static still (handled in control-targets3.css).
 *
 * Simplified 2026-07-05 (founder direction): the two data-model readout segments are
 * dropped. Kept: You £120 vs Market median £132 and the position dial with a plain
 * "Positioned below market" read, plus a short value caption. See the copy doc's §W9
 * and claims-register §2 for what was retired and why.
 */

// Marker position along the visible scale. £120 (you) sits below the £132 median — the
// underpriced case the copy describes. The scale spans ~£104–£148 (padded around both
// values) mapped to 6%→94%; the marker lands at your rate's position.
const SCALE_MIN = 104
const SCALE_MAX = 148
const pos = (v: number) => 6 + ((v - SCALE_MIN) / (SCALE_MAX - SCALE_MIN)) * 88
const YOU_POS = pos(120)
const MEDIAN_POS = pos(132)

export default function MarketPositionStrip({ className }: { className?: string }) {
  const ref = React.useRef<HTMLDivElement | null>(null)
  const [live, setLive] = React.useState(false)

  React.useEffect(() => {
    const el = ref.current
    if (!el) return
    if (typeof IntersectionObserver === 'undefined') {
      setLive(true)
      return
    }
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          setLive(true)
          io.disconnect()
        }
      },
      { threshold: 0.4 }
    )
    io.observe(el)
    return () => io.disconnect()
  }, [])

  return (
    <div
      ref={ref}
      role="img"
      aria-label="A market position readout — your rate £120 against a market median of £132, positioned below the market with room to move up."
      className={cn('mkt3-panel p-5 sm:p-6', className)}
      style={{ '--mkt3-ctm-pos': `${YOU_POS}%` } as React.CSSProperties}
    >
      <div className="mb-6 flex items-center justify-between">
        <span className="mkt3-label">Market Position</span>
      </div>

      {/* the position scale: you against the market median */}
      <div className="relative pt-8 pb-1" aria-hidden="true">
        {/* median marker (fixed reference) */}
        <div className="absolute top-0" style={{ left: `${MEDIAN_POS}%`, transform: 'translateX(-50%)' }}>
          <span className="mb-1.5 block whitespace-nowrap font-mono text-[10px] uppercase tracking-[0.1em] text-slate-400">
            Median
          </span>
          <span className="mx-auto block h-3.5 w-px bg-slate-300" />
        </div>

        {/* your position — slides in once */}
        <div
          className={cn('mkt3-ctm-marker absolute top-0 -translate-x-1/2', live && 'is-live')}
          style={{ top: '1.75rem' }}
        >
          <span className="mx-auto block h-3.5 w-3.5 rounded-full border-2 border-white bg-teal-600 shadow-[0_2px_8px_-1px_rgba(13,148,136,0.6)]" />
          <span className="mt-1 block whitespace-nowrap font-mono text-[11px] font-semibold tabular-nums text-teal-700">
            You £120
          </span>
        </div>

        {/* the track */}
        <div className="relative mt-1 h-2 rounded-full bg-stone-100">
          {/* the gap between you and the median, tinted */}
          <span
            className="absolute inset-y-0 rounded-full bg-teal-200/70"
            style={{ left: `${YOU_POS}%`, width: `${MEDIAN_POS - YOU_POS}%` }}
          />
        </div>
      </div>

      {/* the two readout segments (verbatim: "You £120 · Market median £132") + dial read */}
      <dl className="mt-9 grid grid-cols-2 gap-x-4 gap-y-4">
        {[
          { k: 'You', v: '£120', accent: true },
          { k: 'Market median', v: '£132' },
        ].map((seg) => (
          <div key={seg.k}>
            <dt className="font-mono text-[10px] uppercase tracking-[0.12em] text-slate-400">{seg.k}</dt>
            <dd
              className={cn(
                'mt-1 text-lg font-semibold tabular-nums',
                seg.accent ? 'text-teal-700' : 'text-slate-900'
              )}
            >
              {seg.v}
            </dd>
          </div>
        ))}
      </dl>

      {/* position dial read + a short value caption drawn from the MI copy */}
      <div className="mt-6 border-t border-stone-200/70 pt-4">
        <p className="font-mono text-[10px] font-semibold uppercase tracking-[0.14em] text-teal-700">
          Positioned below market
        </p>
        <p className="mt-1.5 text-sm text-slate-500">Room to move up.</p>
      </div>
    </div>
  )
}
